import { Entity, parseEntityRef } from '@backstage/catalog-model';

/**
 * The BusinessProcess model: SAVR's critical business processes from the
 * BIA/BCM matrix, linked to the systems and vendors that support them.
 * Authored in catalog/processes.yaml.
 *
 * Not a Domain (a System belongs to exactly one Domain, but the BIA matrix is
 * many-to-many) and not a System (which already means both "capability
 * grouping" and "external vendor" in this catalog).
 *
 * This package holds the model and nothing else - the types, the vocabularies
 * and normaliseBusinessProcess, which is the whole of what a process may say.
 * It lives outside both packages so the catalog processor that ingests the
 * YAML and the editor that drafts it run the SAME rules, rather than the
 * frontend growing a second copy that drifts. Only @backstage/catalog-model is
 * imported, and nothing here touches the network or the filesystem, so it runs
 * unchanged in Node and in the browser.
 */

export const BUSINESS_PROCESS_API_VERSION = 'savr.com/v1alpha1';
export const BUSINESS_PROCESS_KIND = 'BusinessProcess';

/**
 * Kinds a process may name as a supporting dependency.
 *
 * Exported because the editor's dependency picker filters the catalog by it.
 * A picker that offered a kind the validator rejects would be offering a
 * choice that cannot be saved.
 */
export const SUPPORTED_TARGET_KINDS = [
  'component',
  'resource',
  'api',
  'system',
];

/**
 * Who performs a step. Only `tech` steps are expected to carry links.
 *
 * Exported so the editor's actor dropdown is this list rather than a copy of
 * it; the same reasoning as SUPPORTED_TARGET_KINDS.
 */
export const STEP_ACTORS = ['tech', 'manual', 'vendor'];

/**
 * How a dependency is reached. `manual` is a human surface - an ops UI someone
 * logs into, an outsourced desk - rather than something our code calls; it
 * fails and escalates differently, and in the BIA it is often the contingency
 * for the automated path rather than a peer of it. Same vocabulary as a step's
 * actor, so the flow model inherits it.
 */
export const LINK_PATHS = ['automated', 'manual'];

/**
 * One (process, resource) pair from the BIA. The MTPD lives on the LINK
 * because it is a property of the pair, not of either side: FNZ Api is
 * 2 minutes for order receipt and 14 days for transfers.
 */
export type SupportLink = {
  /** Entity ref of the supporting component/resource/api/system. */
  target: string;
  /** ISO-8601 duration, e.g. PT2M or P14D. */
  mtpd: string;
  /** The BIA sheet row this link came from, e.g. "SAVR Platform". */
  biaResource: string;
  /** `automated` (default) or `manual`; see LINK_PATHS. */
  path?: string;
  /** Derived by this processor; seconds equivalent of `mtpd`. */
  mtpdSeconds?: number;
  /** Set when the link was derived from a step, naming that step's id. */
  step?: string;
};

/**
 * A link as authored on a step. mtpd and biaResource are optional here: the
 * BIA sets its target per (process, resource), so a step inherits both from
 * the process-level link to the same target rather than restating them.
 */
export type StepLink = Partial<SupportLink> & { target: string };

/**
 * A step in the process flow.
 *
 * `actor` and `fallbackFor` are independent axes and must not be conflated:
 * actor says WHO performs the step, fallbackFor says whether it is on the
 * happy path at all. A manual compliance sign-off can be main path, and an
 * automated retry job can be a fallback.
 */
export type ProcessStep = {
  id: string;
  title: string;
  actor: string;
  /**
   * The step this one is the contingency for, if any. Its presence is what
   * makes a step a fallback - there is deliberately no separate `kind` field,
   * and deliberately no new meaning for the word "manual", which already
   * denotes a LINK_PATHS value, a STEP_ACTORS value and a /processes tab.
   *
   * One level only: a fallback may not name another fallback. That is what
   * lets the UI number it as "3b" against its parent, and stops a chain of
   * fallbacks becoming a second flow nobody reads as one.
   */
  fallbackFor?: string;
  supportedBy?: StepLink[];
};

export type BusinessProcessSpec = {
  owner: string;
  priority: number;
  mtpd: string;
  rto: string;
  marketHoursOnly?: boolean;
  supportedBy?: SupportLink[];
  steps?: ProcessStep[];
  mtpdSeconds?: number;
  rtoSeconds?: number;
};

export function isBusinessProcess(entity: Entity): boolean {
  return (
    entity.apiVersion === BUSINESS_PROCESS_API_VERSION &&
    entity.kind === BUSINESS_PROCESS_KIND
  );
}

/**
 * Seconds in an ISO-8601 duration. Supports the subset the BIA needs -
 * weeks, days, hours, minutes and seconds - and deliberately rejects
 * months and years, whose length is not fixed and so cannot be compared
 * against a measured outage.
 */
export function parseIsoDuration(value: string): number {
  const match =
    /^P(?!$)(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?!$)(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(
      value,
    );
  if (!match) {
    throw new Error(
      `"${value}" is not a supported ISO-8601 duration (use e.g. PT2M, PT4H, P3D, P14D; months and years are not accepted because their length varies)`,
    );
  }
  const [weeks, days, hours, minutes, seconds] = match
    .slice(1)
    .map(part => (part === undefined ? 0 : Number(part)));
  return weeks * 604800 + days * 86400 + hours * 3600 + minutes * 60 + seconds;
}

/**
 * Every support link of a process, as one flat list.
 *
 * Steps REFINE placement; they do not replace the dependency set. A step link
 * inherits mtpd, biaResource and path from the process-level link to the same
 * target - the BIA sets those per (process, resource) and never per step - and
 * a process-level link that no step claims is kept, with no `step`.
 *
 * Keeping the unclaimed ones is what makes incremental mapping possible: the
 * flow can name the four steps someone is sure of without having to invent a
 * step for cross-cutting infrastructure, and no BIA cell can silently vanish
 * from the relations or the matrix by being left unplaced.
 */
export function supportLinksOf(spec: BusinessProcessSpec): SupportLink[] {
  if (!spec.steps?.length) {
    return spec.supportedBy ?? [];
  }
  const byTarget = new Map(
    (spec.supportedBy ?? []).map(link => [link.target, link]),
  );
  const claimed = new Set<string>();
  const seen = new Set<string>();
  const links: SupportLink[] = [];
  for (const step of spec.steps) {
    for (const link of step.supportedBy ?? []) {
      const key = `${step.id}|${link.target}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      claimed.add(link.target);
      const fallback = byTarget.get(link.target);
      links.push({
        ...link,
        step: step.id,
        mtpd: link.mtpd ?? fallback?.mtpd ?? spec.mtpd,
        biaResource: link.biaResource ?? fallback?.biaResource ?? '',
        path: link.path ?? fallback?.path,
        target: link.target,
      });
    }
  }
  for (const link of spec.supportedBy ?? []) {
    if (!claimed.has(link.target)) {
      links.push({ ...link, step: undefined });
    }
  }
  return links;
}

/**
 * Validates and normalises a BusinessProcess in place, returning the entity.
 * Throws with a message naming the offending field - these entities are a
 * compliance-adjacent dataset, so a silent default is worse than a hard fail.
 */
export function normaliseBusinessProcess(entity: Entity): Entity {
  const spec = entity.spec as BusinessProcessSpec | undefined;
  const name = entity.metadata?.name ?? '<unnamed>';
  if (!spec) {
    throw new Error(`BusinessProcess ${name} has no spec`);
  }
  if (typeof spec.owner !== 'string' || !spec.owner) {
    throw new Error(`BusinessProcess ${name} must declare spec.owner`);
  }
  parseEntityRef(spec.owner, {
    defaultKind: 'group',
    defaultNamespace: 'default',
  });
  if (
    !Number.isInteger(spec.priority) ||
    spec.priority < 1 ||
    spec.priority > 4
  ) {
    throw new Error(
      `BusinessProcess ${name} spec.priority must be an integer 1-4 (the BIA scale), got ${spec.priority}`,
    );
  }
  for (const field of ['mtpd', 'rto'] as const) {
    if (typeof spec[field] !== 'string') {
      throw new Error(
        `BusinessProcess ${name} must declare spec.${field} as an ISO-8601 duration`,
      );
    }
  }
  spec.mtpdSeconds = withField(name, 'spec.mtpd', () =>
    parseIsoDuration(spec.mtpd),
  );
  spec.rtoSeconds = withField(name, 'spec.rto', () =>
    parseIsoDuration(spec.rto),
  );

  const stepIds = new Set<string>();
  for (const step of spec.steps ?? []) {
    if (!step.id || !step.title) {
      throw new Error(
        `BusinessProcess ${name} has a step without an id and title`,
      );
    }
    // supportLinksOf dedupes on `${step.id}|${target}`, so two steps sharing
    // an id would silently merge their links instead of failing.
    if (stepIds.has(step.id)) {
      throw new Error(
        `BusinessProcess ${name} declares step ${step.id} twice; step ids identify a step's links and must be unique`,
      );
    }
    stepIds.add(step.id);
    if (!STEP_ACTORS.includes(step.actor)) {
      throw new Error(
        `BusinessProcess ${name} step ${step.id} has actor "${
          step.actor
        }"; expected one of ${STEP_ACTORS.join(', ')}`,
      );
    }
  }

  // fallbackFor is checked in a second pass so a fallback may be authored
  // before the step it covers.
  const byId = new Map((spec.steps ?? []).map(step => [step.id, step]));
  for (const step of spec.steps ?? []) {
    if (step.fallbackFor === undefined) {
      continue;
    }
    if (step.fallbackFor === step.id) {
      throw new Error(
        `BusinessProcess ${name} step ${step.id} is its own fallbackFor`,
      );
    }
    const covered = byId.get(step.fallbackFor);
    if (!covered) {
      throw new Error(
        `BusinessProcess ${name} step ${step.id} has fallbackFor "${step.fallbackFor}", which is not a step of this process`,
      );
    }
    // A fallback for a fallback has no place to be numbered against, and
    // reads as a second flow rather than a contingency on the first.
    if (covered.fallbackFor !== undefined) {
      throw new Error(
        `BusinessProcess ${name} step ${step.id} is a fallback for ${covered.id}, which is itself a fallback; fallbacks cover main-path steps only`,
      );
    }
  }

  const links = supportLinksOf(spec);
  if (links.length === 0) {
    throw new Error(
      `BusinessProcess ${name} declares no supporting dependencies; a process with nothing behind it cannot be assessed`,
    );
  }
  for (const link of links) {
    if (!link.biaResource) {
      throw new Error(
        `BusinessProcess ${name} link to ${link.target} has no biaResource; it names the BIA row the link came from and is what the support matrix groups columns by`,
      );
    }
    const ref = withField(name, `link ${link.target}`, () =>
      parseEntityRef(link.target, {
        defaultKind: 'component',
        defaultNamespace: 'default',
      }),
    );
    if (!SUPPORTED_TARGET_KINDS.includes(ref.kind.toLocaleLowerCase('en-US'))) {
      throw new Error(
        `BusinessProcess ${name} link to ${link.target} targets kind "${
          ref.kind
        }"; expected one of ${SUPPORTED_TARGET_KINDS.join(', ')}`,
      );
    }
    if (link.path !== undefined && !LINK_PATHS.includes(link.path)) {
      throw new Error(
        `BusinessProcess ${name} link to ${link.target} has path "${
          link.path
        }"; expected one of ${LINK_PATHS.join(', ')}`,
      );
    }
    link.mtpdSeconds = withField(name, `link ${link.target} mtpd`, () =>
      parseIsoDuration(link.mtpd),
    );
  }
  // Steps are the source of truth once present; publish their union so
  // consumers never have to know which shape the entity was authored in.
  if (spec.steps?.length) {
    spec.supportedBy = links;
  }
  return entity;
}

function withField<T>(name: string, field: string, read: () => T): T {
  try {
    return read();
  } catch (error) {
    throw new Error(
      `BusinessProcess ${name} ${field}: ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}
